import { useEffect, useState } from "react";
import { api, formatMoney, formatApiError, isFreemiumError } from "@/lib/api";
import { BulkAssignModal } from "./BulkAssignModal";
import { UpgradePrompt } from "@/components/UpgradePrompt";

const PHASES = ["challenge", "verification", "funded", "live", "demo"];
const STATUSES = ["active", "passed", "breached", "paused", "archived"];
const CURRENCIES = ["USD", "EUR", "GBP", "JPY", "AUD", "CAD", "CHF", "INR"];
const COMMON_FIRMS = ["FTMO", "The5%ers", "MyForexFunds", "FundedNext", "TopstepFX", "MyFundedFX", "E8 Funding", "Personal Broker"];

const EMPTY = { prop_firm: "", label: "", account_size: 100000, currency: "USD", phase: "challenge", status: "active", daily_loss_pct: 5, max_drawdown_pct: 10 };

const badgeCls = (status) => {
  switch (status) {
    case "active": return "border-[#5E7A62]/60 text-[#7A9E80]";
    case "passed": return "border-[#CFA66A]/60 text-[#CFA66A]";
    case "funded": return "border-[#7A9E80]/60 text-[#7A9E80]";
    case "breached": return "border-[#8C4A42]/60 text-[#B56258]";
    case "paused": return "border-[#5C7C8A]/60 text-[#5C7C8A]";
    case "archived": return "border-[#2E2A25] text-[#A39A8E]";
    default: return "border-[#2E2A25] text-[#A39A8E]";
  }
};

export function AccountsSection() {
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | 'new' | account.id
  const [form, setForm] = useState(EMPTY);
  const [error, setError] = useState(null);
  const [breaches, setBreaches] = useState({}); // id -> {breaches, day_pnl, total_pnl, daily_limit, max_drawdown}
  const [showBulk, setShowBulk] = useState(false);
  const [unassignedCount, setUnassignedCount] = useState(0);
  const [upgradeMsg, setUpgradeMsg] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/accounts");
      setAccounts(data);
      // Fetch prop status for each active account
      const statuses = {};
      await Promise.all(
        data.filter((a) => a.status !== "archived").map(async (a) => {
          try {
            const { data: s } = await api.get(`/auth/prop-status?account_id=${a.id}`);
            statuses[a.id] = s;
          } catch (_e) { /* ignore per-account */ }
        })
      );
      setBreaches(statuses);
      // Also fetch unassigned trades count
      try {
        const { data: u } = await api.get("/journal/unassigned-trades");
        setUnassignedCount(u.count || 0);
      } catch (_e) { /* ignore */ }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const startNew = () => { setForm(EMPTY); setEditing("new"); setError(null); };
  const startEdit = (a) => {
    setForm({
      prop_firm: a.prop_firm, label: a.label || "", account_size: a.account_size, currency: a.currency,
      phase: a.phase, status: a.status, daily_loss_pct: a.daily_loss_pct, max_drawdown_pct: a.max_drawdown_pct,
    });
    setEditing(a.id);
    setError(null);
  };

  const cancel = () => { setEditing(null); setError(null); };

  const save = async () => {
    setError(null);
    setUpgradeMsg(null);
    try {
      const payload = { ...form, account_size: parseFloat(form.account_size), daily_loss_pct: parseFloat(form.daily_loss_pct), max_drawdown_pct: parseFloat(form.max_drawdown_pct) };
      if (!payload.prop_firm.trim()) { setError("Prop firm is required."); return; }
      if (editing === "new") await api.post("/accounts", payload);
      else await api.patch(`/accounts/${editing}`, payload);
      setEditing(null);
      load();
    } catch (e) {
      if (isFreemiumError(e)) {
        setUpgradeMsg(formatApiError(e.response?.data?.detail));
        setEditing(null);
      } else {
        setError(formatApiError(e.response?.data?.detail) || e.message);
      }
    }
  };

  const del = async (a) => {
    if (!confirm(`Delete account "${a.label}"? Historical trades stay put — this only removes the account definition.`)) return;
    await api.delete(`/accounts/${a.id}`);
    load();
  };

  return (
    <section data-testid="accounts-section" className="mt-10 border border-[#2E2A25] bg-[#1A1815] p-6 rounded-sm">
      <div className="flex items-baseline justify-between mb-4 gap-4 flex-wrap">
        <div>
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#CFA66A]">— Portfolio</p>
          <h2 className="font-head text-2xl text-[#EBE5D9] mt-1">My accounts</h2>
          <p className="text-xs text-[#A39A8E] mt-1">Track every prop-firm challenge, funded account, and live broker in one place.</p>
        </div>
        {editing === null && (
          <div className="flex gap-2 shrink-0">
            {unassignedCount > 0 && accounts.some((a) => a.status !== "archived") && (
              <button
                data-testid="bulk-assign-open"
                onClick={() => setShowBulk(true)}
                className="bg-transparent border border-[#CFA66A] text-[#CFA66A] hover:bg-[#CFA66A]/10 px-4 py-2 rounded-sm font-mono text-xs uppercase tracking-widest transition-colors"
              >
                Assign {unassignedCount} unassigned
              </button>
            )}
            <button data-testid="accounts-add" onClick={startNew} className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-4 py-2 rounded-sm hover:bg-[#EBE5D9] transition-colors font-mono text-xs uppercase tracking-widest">
              + Add account
            </button>
          </div>
        )}
      </div>

      {loading ? (
        <p className="text-xs font-mono text-[#A39A8E]">Loading…</p>
      ) : (
        <>
          {upgradeMsg && <UpgradePrompt message={upgradeMsg} onDismiss={() => setUpgradeMsg(null)} />}
          {accounts.length === 0 && editing === null && !upgradeMsg && (
            <p className="text-sm text-[#A39A8E] italic border border-dashed border-[#2E2A25] p-6 text-center rounded-sm">
              No accounts yet. Add your first prop firm or broker account to start portfolio tracking.
            </p>
          )}

          <div className="space-y-3">
            {accounts.map((a) => {
              const status = breaches[a.id];
              const isEditing = editing === a.id;
              if (isEditing) return <AccountForm key={a.id} form={form} setForm={setForm} onSave={save} onCancel={cancel} error={error} editing={a.id} />;
              return (
                <div key={a.id} data-testid={`account-card-${a.id}`} className="border border-[#2E2A25] bg-[#0F0E0C] p-4 rounded-sm">
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <p className="font-head text-lg text-[#EBE5D9]">{a.label}</p>
                        <span className={`font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border rounded-sm ${badgeCls(a.status)}`}>{a.status}</span>
                        <span className="font-mono text-[9px] uppercase tracking-widest px-1.5 py-0.5 border border-[#CFA66A]/40 text-[#CFA66A] rounded-sm">{a.phase}</span>
                      </div>
                      <p className="font-mono text-xs text-[#A39A8E] mt-1">
                        {a.prop_firm} · {formatMoney(a.account_size, a.currency)} · DL {a.daily_loss_pct}% · MDD {a.max_drawdown_pct}%
                      </p>
                      {status && (
                        <div className="mt-2 flex gap-4 flex-wrap font-mono text-[10px]">
                          <span className="text-[#A39A8E]">Day P&L: <span className={status.day_pnl > 0 ? "text-[#7A9E80]" : status.day_pnl < 0 ? "text-[#B56258]" : "text-[#EBE5D9]"}>{formatMoney(status.day_pnl, a.currency)}</span></span>
                          <span className="text-[#A39A8E]">Total: <span className={status.total_pnl > 0 ? "text-[#7A9E80]" : status.total_pnl < 0 ? "text-[#B56258]" : "text-[#EBE5D9]"}>{formatMoney(status.total_pnl, a.currency)}</span></span>
                          {status.breaches?.length > 0 && (
                            <span data-testid={`account-breach-${a.id}`} className="text-[#B56258] font-medium">⚠ {status.breaches.map((b) => b.type).join(", ")}</span>
                          )}
                        </div>
                      )}
                      {a.analytics && a.analytics.trade_count > 0 && (
                        <div data-testid={`account-analytics-${a.id}`} className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                          <MiniStat label="Trades" value={a.analytics.trade_count} />
                          <MiniStat label="Win rate" value={`${a.analytics.win_rate}%`} tone={a.analytics.win_rate >= 50 ? "good" : "warn"} />
                          <MiniStat label="Expectancy" value={formatMoney(a.analytics.expectancy, a.currency)} tone={a.analytics.expectancy > 0 ? "good" : a.analytics.expectancy < 0 ? "bad" : "neutral"} />
                          <MiniStat label="Best pair" value={a.analytics.best_pair ? `${a.analytics.best_pair.pair} ${formatMoney(a.analytics.best_pair.pnl, a.currency)}` : "—"} tone="neutral" />
                        </div>
                      )}
                    </div>
                    <div className="flex gap-2 shrink-0">
                      <button data-testid={`account-edit-${a.id}`} onClick={() => startEdit(a)} className="font-mono text-[10px] uppercase tracking-widest text-[#CFA66A] hover:text-[#EBE5D9]">edit</button>
                      <button data-testid={`account-delete-${a.id}`} onClick={() => del(a)} className="font-mono text-[10px] uppercase tracking-widest text-[#8C4A42] hover:text-[#B56258]">delete</button>
                    </div>
                  </div>
                </div>
              );
            })}

            {editing === "new" && <AccountForm form={form} setForm={setForm} onSave={save} onCancel={cancel} error={error} editing="new" />}
          </div>
        </>
      )}

      {showBulk && (
        <BulkAssignModal
          accounts={accounts.filter((a) => a.status !== "archived")}
          onClose={() => setShowBulk(false)}
          onSaved={() => load()}
        />
      )}
    </section>
  );
}

function MiniStat({ label, value, tone = "neutral" }) {
  const cls = tone === "good"
    ? "text-[#7A9E80]"
    : tone === "bad"
      ? "text-[#B56258]"
      : tone === "warn"
        ? "text-[#CFA66A]"
        : "text-[#EBE5D9]";
  return (
    <div className="border border-[#2E2A25] bg-[#1A1815] p-2 rounded-sm">
      <p className="font-mono text-[9px] uppercase tracking-widest text-[#A39A8E]">{label}</p>
      <p className={`font-mono text-xs mt-0.5 truncate ${cls}`}>{value}</p>
    </div>
  );
}

function AccountForm({ form, setForm, onSave, onCancel, error, editing }) {
  const upd = (field, val) => setForm({ ...form, [field]: val });
  return (
    <div data-testid={`account-form-${editing}`} className="border border-[#CFA66A]/40 bg-[#0F0E0C] p-4 rounded-sm space-y-3">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <FormField label="Prop firm">
          <input data-testid="account-form-firm" list="firm-suggestions" value={form.prop_firm} onChange={(e) => upd("prop_firm", e.target.value)} placeholder="FTMO, FundedNext, …" className="input" />
          <datalist id="firm-suggestions">
            {COMMON_FIRMS.map((f) => <option key={f} value={f} />)}
          </datalist>
        </FormField>
        <FormField label="Label (optional)">
          <input data-testid="account-form-label" value={form.label} onChange={(e) => upd("label", e.target.value)} placeholder='e.g. "FTMO 100k Ph2"' className="input" />
        </FormField>
        <FormField label="Account size">
          <input data-testid="account-form-size" type="number" step="1000" value={form.account_size} onChange={(e) => upd("account_size", e.target.value)} className="input" />
        </FormField>
        <FormField label="Currency">
          <select data-testid="account-form-currency" value={form.currency} onChange={(e) => upd("currency", e.target.value)} className="input">
            {CURRENCIES.map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
        </FormField>
        <FormField label="Phase">
          <select data-testid="account-form-phase" value={form.phase} onChange={(e) => upd("phase", e.target.value)} className="input">
            {PHASES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </FormField>
        <FormField label="Status">
          <select data-testid="account-form-status" value={form.status} onChange={(e) => upd("status", e.target.value)} className="input">
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </FormField>
        <FormField label="Daily loss %">
          <input data-testid="account-form-daily" type="number" step="0.1" value={form.daily_loss_pct} onChange={(e) => upd("daily_loss_pct", e.target.value)} className="input" />
        </FormField>
        <FormField label="Max drawdown %">
          <input data-testid="account-form-mdd" type="number" step="0.1" value={form.max_drawdown_pct} onChange={(e) => upd("max_drawdown_pct", e.target.value)} className="input" />
        </FormField>
      </div>
      {error && <p className="font-mono text-xs text-[#B56258]">{error}</p>}
      <div className="flex gap-2 justify-end">
        <button data-testid="account-form-cancel" onClick={onCancel} className="font-mono text-xs uppercase tracking-widest text-[#A39A8E] hover:text-[#EBE5D9] px-3 py-1.5">Cancel</button>
        <button data-testid="account-form-save" onClick={onSave} className="bg-[#CFA66A] text-[#0F0E0C] font-medium px-4 py-1.5 rounded-sm hover:bg-[#EBE5D9] transition-colors font-mono text-xs uppercase tracking-widest">
          {editing === "new" ? "Create account" : "Save changes"}
        </button>
      </div>
      <style>{`.input{width:100%;background:#0F0E0C;border:1px solid #2E2A25;color:#EBE5D9;padding:.5rem .75rem;border-radius:.125rem;font-family:ui-monospace,SFMono-Regular,Menlo,monospace;font-size:.875rem}.input:focus{outline:none;border-color:#CFA66A}`}</style>
    </div>
  );
}

function FormField({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#A39A8E] block mb-1">{label}</label>
      {children}
    </div>
  );
}
